import { useState, useCallback } from "react";
import {
  Film,
  Sparkles,
  Trash2,
  Pencil,
  ChevronLeft,
  ChevronRight,
  Eye,
  EyeOff,
} from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { ActionButton } from "@/components/ui/action-button";
import { Spinner } from "@/components/ui/spinner";
import { PillTabs } from "@/components/ui/pill-tabs";
import { FilterBar, FilterSearch } from "@/components/ui/filter-bar";
import { BulkActionBar } from "@/components/ui/bulk-action-bar";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TableCard } from "@/components/ui/table-card";
import { Checkbox } from "@/components/ui/checkbox";
import {
  useMovies,
  useDeleteMovie,
  useEnrichMovies,
  useUpdateMovieStatus,
} from "@/hooks/useMovies";
import { MovieEditDialog } from "@/components/movies/MovieEditDialog";
import { CreateMovieDialog } from "@/components/movies/CreateMovieDialog";
import {
  FilmwebIcon,
  ImdbIcon,
  RottenTomatoesIcon,
  RatingValue,
} from "@/components/movies/RatingIcons";
import { StatusBadge } from "@/components/events/StatusBadge";
import { ImagePreview } from "@/components/ui/image-preview";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Movie, EventStatus } from "@/types";

function MovieRow({
  movie,
  selected,
  onToggle,
  onEdit,
  onDelete,
  onStatusChange,
  isDeleting,
  isUpdatingStatus,
}: {
  movie: Movie;
  selected: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onStatusChange: (status: string) => void;
  isDeleting: boolean;
  isUpdatingStatus: boolean;
}) {
  const date = new Date(movie.date);
  const dateStr = date.toLocaleDateString("pl-PL", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

  return (
    <TableRow>
      <TableCell className="w-10">
        <Checkbox checked={selected} onCheckedChange={onToggle} />
      </TableCell>
      <TableCell className="w-[70px] min-w-[70px]">
        {movie.coverImage ? (
          <ImagePreview src={movie.coverImage.thumb} alt={movie.title} />
        ) : (
          <div className="w-[70px] h-[70px] bg-slate-100 rounded flex items-center justify-center">
            <Film className="h-5 w-5 text-slate-400" />
          </div>
        )}
      </TableCell>
      <TableCell>
        <div className="font-medium text-sm">{movie.title}</div>
        {movie.originalTitle && movie.originalTitle !== movie.title && (
          <div className="text-xs text-muted-foreground italic">{movie.originalTitle}</div>
        )}
      </TableCell>
      <TableCell className="text-sm text-muted-foreground">{dateStr}</TableCell>
      <TableCell className="text-sm text-muted-foreground">{movie.year ?? "—"}</TableCell>
      <TableCell className="text-sm text-muted-foreground">
        {movie.runtime ? `${movie.runtime} min` : "—"}
      </TableCell>
      <TableCell>
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2.5">
            {movie.filmwebRating && (
              <RatingValue
                icon={<FilmwebIcon />}
                value={movie.filmwebRating}
                title={`Filmweb: ${movie.filmwebRating}`}
              />
            )}
            {movie.imdbRating && (
              <RatingValue
                icon={<ImdbIcon />}
                value={movie.imdbRating}
                title={`IMDb: ${movie.imdbRating}`}
              />
            )}
            {movie.rtScore && (
              <RatingValue
                icon={<RottenTomatoesIcon />}
                value={movie.rtScore}
                title={`Rotten Tomatoes: ${movie.rtScore}`}
              />
            )}
            {!movie.filmwebRating && !movie.imdbRating && !movie.rtScore && (
              <span className="text-sm text-muted-foreground">—</span>
            )}
          </div>
          {movie.lastEnrichedAt && (
            <span
              className="text-[10px] text-muted-foreground"
              title={new Date(movie.lastEnrichedAt).toLocaleString("pl-PL")}
            >
              wzbogacono: {new Date(movie.lastEnrichedAt).toLocaleDateString("pl-PL")}
            </span>
          )}
        </div>
      </TableCell>
      <TableCell>
        <Select value={movie.status} onValueChange={onStatusChange} disabled={isUpdatingStatus}>
          <SelectTrigger className="h-8 w-auto gap-1.5 border-0 bg-transparent px-1.5 shadow-none hover:bg-black/5 focus:ring-0 focus:ring-offset-0 [&>span]:line-clamp-none">
            <SelectValue>
              <StatusBadge status={movie.status as EventStatus} />
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="pending">Oczekujące</SelectItem>
            <SelectItem value="approved">Zatwierdzone</SelectItem>
            <SelectItem value="rejected">Odrzucone</SelectItem>
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell>
        <div className="flex items-center gap-1">
          <ActionButton tooltip="Edytuj" className="h-8 w-8" onClick={onEdit}>
            <Pencil className="h-3.5 w-3.5" />
          </ActionButton>
          <ActionButton
            tooltip="Usuń"
            className="h-8 w-8 text-destructive hover:text-destructive"
            onClick={onDelete}
            disabled={isDeleting}
          >
            {isDeleting ? <Spinner className="h-3.5 w-3.5" /> : <Trash2 className="h-3.5 w-3.5" />}
          </ActionButton>
        </div>
      </TableCell>
    </TableRow>
  );
}

export default function MoviesPage() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("all");
  const [hidePast, setHidePast] = useState(true);
  const [limit, setLimit] = useState(25);
  const [cursorStack, setCursorStack] = useState<string[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [updatingStatusId, setUpdatingStatusId] = useState<string | null>(null);
  const [editingMovie, setEditingMovie] = useState<Movie | null>(null);

  const handleSearchChange = useCallback((v: string) => {
    setSearch(v);
    setCursorStack([]);
  }, []);

  const cursor = cursorStack.at(-1);
  const { data: result, isLoading, error } = useMovies({ search, status, hidePast, cursor, limit });
  const movies = result?.data ?? [];
  const hasMore = result?.hasMore ?? false;
  const nextCursor = result?.nextCursor ?? null;

  const deleteMovie = useDeleteMovie();
  const updateMovieStatus = useUpdateMovieStatus();
  const enrichMovies = useEnrichMovies();

  const handleStatusFilterChange = (v: string) => {
    setStatus(v);
    setCursorStack([]);
  };
  const handleHidePastChange = () => {
    setHidePast((prev) => !prev);
    setCursorStack([]);
  };
  const handleLimitChange = (v: string) => {
    setLimit(Number(v));
    setCursorStack([]);
  };
  const goNext = () => {
    if (nextCursor) setCursorStack((prev) => [...prev, nextCursor]);
  };
  const goPrev = () => setCursorStack((prev) => prev.slice(0, -1));
  const page = cursorStack.length + 1;

  const toggleAll = () => {
    if (selected.size === movies.length) setSelected(new Set());
    else setSelected(new Set(movies.map((m) => m.id)));
  };

  const toggleOne = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try {
      await deleteMovie.mutateAsync(id);
      setSelected((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      toast.success("Film usunięty");
    } catch (err) {
      toast.error("Nie udało się usunąć filmu", {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setDeletingId(null);
    }
  };

  const handleStatusChange = async (id: string, status: string) => {
    setUpdatingStatusId(id);
    try {
      await updateMovieStatus.mutateAsync({ id, status });
    } catch (err) {
      toast.error("Nie udało się zmienić statusu", {
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleBulkDelete = async () => {
    const ids = [...selected];
    for (const id of ids) {
      try {
        await deleteMovie.mutateAsync(id);
      } catch {
        // continue
      }
    }
    setSelected(new Set());
    toast.success(`Usunięto ${ids.length} filmów`);
  };

  const handleBulkEnrich = async () => {
    const ids = [...selected];
    try {
      const result = await enrichMovies.mutateAsync(ids);
      const ok = result.results.filter((r) => r.ok).length;
      const total = result.results.length;
      if (ok === total) {
        toast.success(`Wzbogacono ${ok}/${total} filmów`);
      } else {
        toast.warning(`Wzbogacono ${ok}/${total} filmów`, {
          description: "Filmweb nie znalazł pozostałych tytułów.",
        });
      }
      setSelected(new Set());
    } catch (err) {
      toast.error("Nie udało się wzbogacić filmów", {
        description: err instanceof Error ? err.message : undefined,
      });
    }
  };

  return (
    <div>
      <PageHeader
        title="Filmy"
        description="Zarządzaj filmami kinowymi"
        action={<CreateMovieDialog />}
      />

      <FilterBar>
        <PillTabs
          value={status}
          onChange={handleStatusFilterChange}
          options={[
            { value: "all", label: "Wszystkie" },
            { value: "pending", label: "Oczekujące" },
            { value: "approved", label: "Zatwierdzone" },
            { value: "rejected", label: "Odrzucone" },
          ]}
        />
        <FilterSearch
          value={search}
          onChange={handleSearchChange}
          placeholder="Szukaj po tytule..."
        />
        <Button variant="outline" className="h-10 gap-2" onClick={handleHidePastChange}>
          {hidePast ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          {hidePast ? "Pokaż archiwalne" : "Ukryj archiwalne"}
        </Button>
      </FilterBar>

      {isLoading && (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="h-12 rounded-xl bg-white animate-pulse border border-black/5" />
          ))}
        </div>
      )}
      {error && <p className="text-destructive">Błąd ładowania: {error.message}</p>}
      {!isLoading && movies.length === 0 && (
        <p className="text-center py-12 text-muted-foreground">Brak filmów.</p>
      )}
      {!isLoading && movies.length > 0 && (
        <TableCard>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={selected.size === movies.length && movies.length > 0}
                    onCheckedChange={toggleAll}
                  />
                </TableHead>
                <TableHead className="w-[70px]"></TableHead>
                <TableHead>Tytuł</TableHead>
                <TableHead>Data</TableHead>
                <TableHead>Rok</TableHead>
                <TableHead>Czas</TableHead>
                <TableHead>Oceny</TableHead>
                <TableHead>Status</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {movies.map((movie) => (
                <MovieRow
                  key={movie.id}
                  movie={movie}
                  selected={selected.has(movie.id)}
                  onToggle={() => toggleOne(movie.id)}
                  onEdit={() => setEditingMovie(movie)}
                  onDelete={() => handleDelete(movie.id)}
                  onStatusChange={(status) => handleStatusChange(movie.id, status)}
                  isDeleting={deletingId === movie.id}
                  isUpdatingStatus={updatingStatusId === movie.id}
                />
              ))}
            </TableBody>
          </Table>
        </TableCard>
      )}

      {!isLoading && (movies.length > 0 || cursorStack.length > 0) && (
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <span>Strona {page}</span>
            <Select value={String(limit)} onValueChange={handleLimitChange}>
              <SelectTrigger className="w-24 h-8">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
            <span>na stronę</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={goPrev}
              disabled={cursorStack.length === 0}
            >
              <ChevronLeft className="h-4 w-4" />
              Poprzednia
            </Button>
            <Button variant="outline" size="sm" onClick={goNext} disabled={!hasMore}>
              Następna
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      <BulkActionBar count={selected.size} onClear={() => setSelected(new Set())}>
        <button
          type="button"
          className="bulk-action-btn"
          disabled={enrichMovies.isPending}
          onClick={handleBulkEnrich}
        >
          <Sparkles className="h-3.5 w-3.5" /> Wzbogać
        </button>
        <button
          type="button"
          className="bulk-action-btn"
          data-tone="danger"
          disabled={deleteMovie.isPending}
          onClick={handleBulkDelete}
        >
          <Trash2 className="h-3.5 w-3.5" /> Usuń zaznaczone
        </button>
      </BulkActionBar>

      <MovieEditDialog
        movie={editingMovie}
        open={editingMovie !== null}
        onOpenChange={(open) => {
          if (!open) setEditingMovie(null);
        }}
      />
    </div>
  );
}
